import React, { useEffect, useMemo, useState } from "react";
import {
  approveEvidence,
  deleteEvidence,
  getEvidence,
  getEvidenceSummary,
  rejectEvidence,
  updateEvidence,
} from "../api/evidenceApi";
import { Button, Card, Input, Select, Textarea } from "../components/ui";
import PageHeader from "../components/ui/PageHeader";
import StatCard from "../components/ui/StatCard";
import DataTable from "../components/ui/DataTable";
import EmptyState from "../components/ui/EmptyState";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import Section from "../components/ui/Section";
import Badge from "../components/ui/Badge";
import { colors, spacing } from "../components/ui/theme";
import { addNotification } from "../services/notificationService";

const emptySummary = {
  total: 0,
  pendingReview: 0,
  approved: 0,
  rejected: 0,
  attached: 0,
  published: 0,
  averageConfidence: 0,
};

export default function EvidenceLibraryPage() {
  const [summary, setSummary] = useState(emptySummary);
  const [evidence, setEvidence] = useState([]);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    esrsStandard: "",
    disclosure: "",
  });
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [reviewComments, setReviewComments] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function loadEvidence(nextFilters = filters) {
    try {
      setLoading(true);
      setError("");

      const [summaryData, evidenceData] = await Promise.all([
        getEvidenceSummary(),
        getEvidence(nextFilters),
      ]);

      setSummary({ ...emptySummary, ...(summaryData || {}) });
      setEvidence(Array.isArray(evidenceData) ? evidenceData : []);
    } catch (err) {
      setError(err.message || "Failed to load evidence.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadEvidence();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      {
        key: "esrsStandard",
        label: "ESRS",
        render: (item) => (
          <Badge tone="primary" size="sm">
            {item.esrsStandard || "N/A"}
          </Badge>
        ),
      },
      {
        key: "disclosure",
        label: "Disclosure",
        render: (item) => item.disclosure || "N/A",
      },
      {
        key: "document",
        label: "Document",
        render: (item) => item.document?.originalName || "Unknown document",
      },
      {
        key: "confidence",
        label: "Confidence",
        render: (item) => `${Number(item.confidence || 0)}%`,
      },
      {
        key: "status",
        label: "Status",
        render: (item) => (
          <Badge tone={statusTone(item.status)} size="sm">
            {formatStatus(item.status)}
          </Badge>
        ),
      },
      {
        key: "report",
        label: "Report",
        render: (item) =>
          item.report
            ? `${item.report.companyName} (${item.report.reportingYear})`
            : "Not linked",
      },
      {
        key: "action",
        label: "Action",
        render: (item) => (
          <Button
            variant="secondary"
            onClick={(event) => {
              event.stopPropagation();
              openEvidence(item);
            }}
            style={{ padding: "8px 12px" }}
          >
            View
          </Button>
        ),
      },
    ],
    []
  );

  function openEvidence(item) {
    setSelectedEvidence(item);
    setEditing(false);
    setReviewComments(item.reviewComments || "");
    setEditForm({
      esrsStandard: item.esrsStandard || "",
      disclosure: item.disclosure || "",
      section: item.section || "",
      quote: item.quote || "",
      reason: item.reason || "",
      confidence: item.confidence || 0,
      reviewComments: item.reviewComments || "",
    });
  }

  function clearNotices() {
    setMessage("");
    setError("");
  }

  async function handleSearch(event) {
    event.preventDefault();
    clearNotices();
    await loadEvidence(filters);
  }

  async function handleResetFilters() {
    const reset = {
      search: "",
      status: "",
      esrsStandard: "",
      disclosure: "",
    };
    setFilters(reset);
    clearNotices();
    await loadEvidence(reset);
  }

  async function handleUpdateEvidence() {
    if (!selectedEvidence?._id) return;

    try {
      setSaving(true);
      clearNotices();

      const result = await updateEvidence(selectedEvidence._id, {
        ...editForm,
        confidence: Number(editForm.confidence || 0),
      });

      setMessage("Evidence updated successfully.");
      setSelectedEvidence(result.evidence);
      setEditing(false);
      await loadEvidence();
    } catch (err) {
      setError(err.message || "Failed to update evidence.");
    } finally {
      setSaving(false);
    }
  }

  async function handleApproveEvidence() {
    if (!selectedEvidence?._id) return;

    try {
      setSaving(true);
      clearNotices();
      const result = await approveEvidence(
        selectedEvidence._id,
        reviewComments
      );
      setMessage(result.message || "Evidence approved successfully.");
      setSelectedEvidence(result.evidence);

      addNotification({
        type: "evidence",
        priority: "normal",
        title:
          result.evidence?.status === "published"
            ? "Evidence approved and published"
            : "Evidence approved",
        message: `${result.evidence?.esrsStandard || selectedEvidence.esrsStandard || "ESRS"} ${result.evidence?.disclosure || selectedEvidence.disclosure || "evidence"} was approved successfully.`,
        targetPage: "Evidence Library"
      });

      await loadEvidence();
    } catch (err) {
      setError(err.message || "Failed to approve evidence.");
    } finally {
      setSaving(false);
    }
  }

  async function handleRejectEvidence() {
    if (!selectedEvidence?._id) return;

    try {
      setSaving(true);
      clearNotices();
      const result = await rejectEvidence(
        selectedEvidence._id,
        reviewComments
      );
      setMessage(result.message || "Evidence rejected successfully.");
      setSelectedEvidence(result.evidence);

      addNotification({
        type: "evidence",
        priority: "high",
        title: "Evidence rejected",
        message: `${result.evidence?.esrsStandard || selectedEvidence.esrsStandard || "ESRS"} ${result.evidence?.disclosure || selectedEvidence.disclosure || "evidence"} was rejected${reviewComments ? `: ${reviewComments}` : "."}`,
        targetPage: "Evidence Library"
      });

      await loadEvidence();
    } catch (err) {
      setError(err.message || "Failed to reject evidence.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteEvidence() {
    if (!selectedEvidence?._id) return;

    const confirmed = window.confirm(
      "Delete this evidence item? This action cannot be undone."
    );
    if (!confirmed) return;

    try {
      setSaving(true);
      clearNotices();
      await deleteEvidence(selectedEvidence._id);
      setMessage("Evidence deleted successfully.");
      setSelectedEvidence(null);
      await loadEvidence();
    } catch (err) {
      setError(err.message || "Failed to delete evidence.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.page}>
      <PageHeader
        title="Evidence Library"
        subtitle="Review, validate and govern AI-extracted ESG evidence across reports and source documents."
        actions={
          <Button variant="secondary" onClick={() => loadEvidence()} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        }
      />

      <Notice tone="error" message={error} />
      <Notice tone="success" message={message} />

      <div style={styles.metricsGrid}>
        <StatCard title="Total Evidence" value={summary.total} />
        <StatCard title="Pending Review" value={summary.pendingReview} badge="Queue" tone="warning" />
        <StatCard title="Published" value={summary.published} badge="Live" tone="success" />
        <StatCard title="Attached" value={summary.attached} />
        <StatCard title="Average Confidence" value={`${summary.averageConfidence}%`} />
      </div>

      <Section
        title="Search and filters"
        subtitle="Find evidence by status, ESRS standard, disclosure or keyword."
      >
        <Card>
          <form onSubmit={handleSearch} style={styles.filters}>
            <Input
              placeholder="Search quote, section, reason or disclosure..."
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
            />

            <Select
              value={filters.status}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  status: event.target.value,
                }))
              }
            >
              <option value="">All statuses</option>
              <option value="pending_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="attached">Attached</option>
              <option value="rejected">Rejected</option>
              <option value="published">Published</option>
            </Select>

            <Select
              value={filters.esrsStandard}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  esrsStandard: event.target.value,
                }))
              }
            >
              <option value="">All ESRS standards</option>
              <option value="ESRS 2">ESRS 2</option>
              <option value="E1">E1</option>
              <option value="E2">E2</option>
              <option value="E3">E3</option>
              <option value="E4">E4</option>
              <option value="E5">E5</option>
              <option value="S1">S1</option>
              <option value="S2">S2</option>
              <option value="S3">S3</option>
              <option value="S4">S4</option>
              <option value="G1">G1</option>
            </Select>

            <Input
              placeholder="Disclosure, e.g. GOV-1"
              value={filters.disclosure}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  disclosure: event.target.value,
                }))
              }
            />

            <div style={styles.filterActions}>
              <Button type="submit" disabled={loading}>
                Apply filters
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleResetFilters}
                disabled={loading}
              >
                Reset
              </Button>
            </div>
          </form>
        </Card>
      </Section>

      <div style={styles.contentGrid}>
        <Section
          title="Evidence items"
          subtitle={`${evidence.length} record${evidence.length === 1 ? "" : "s"} found`}
          style={{ marginBottom: 0 }}
        >
          <Card style={{ padding: 0, overflow: "hidden" }}>
            {loading ? (
              <LoadingSpinner label="Loading evidence..." />
            ) : evidence.length === 0 ? (
              <EmptyState
                icon="📚"
                title="No evidence found"
                message="Upload and analyse a sustainability document, or reset your filters to view available evidence."
                actionLabel="Reset filters"
                onAction={handleResetFilters}
                style={{ border: "none", boxShadow: "none" }}
              />
            ) : (
              <DataTable
                columns={columns}
                data={evidence}
                onRowClick={openEvidence}
                emptyTitle="No evidence found"
                emptyMessage="No records match the current filters."
              />
            )}
          </Card>
        </Section>

        <Section
          title="Evidence details"
          subtitle="Review the source, status and approval history."
          style={{ marginBottom: 0 }}
        >
          <Card>
            {!selectedEvidence ? (
              <EmptyState
                icon="🔎"
                title="Select evidence"
                message="Choose an evidence item from the table to inspect its quote, confidence, source and review controls."
                style={{ border: "none", boxShadow: "none", padding: spacing.xl }}
              />
            ) : (
              <EvidenceDetails
                evidence={selectedEvidence}
                editing={editing}
                setEditing={setEditing}
                editForm={editForm}
                setEditForm={setEditForm}
                reviewComments={reviewComments}
                setReviewComments={setReviewComments}
                saving={saving}
                onSave={handleUpdateEvidence}
                onApprove={handleApproveEvidence}
                onReject={handleRejectEvidence}
                onDelete={handleDeleteEvidence}
              />
            )}
          </Card>
        </Section>
      </div>
    </div>
  );
}

function EvidenceDetails({
  evidence,
  editing,
  setEditing,
  editForm,
  setEditForm,
  reviewComments,
  setReviewComments,
  saving,
  onSave,
  onApprove,
  onReject,
  onDelete,
}) {
  return (
    <div>
      <div style={styles.detailHeader}>
        <div>
          <div style={styles.detailTitleRow}>
            <h2 style={styles.detailTitle}>
              {evidence.esrsStandard || "Evidence"} {evidence.disclosure || ""}
            </h2>
            <Badge tone={statusTone(evidence.status)} size="sm">
              {formatStatus(evidence.status)}
            </Badge>
          </div>
          <p style={styles.detailMeta}>
            Version {evidence.version || 1} · {evidence.confidence || 0}% confidence
          </p>
        </div>

        <Button
          variant="secondary"
          onClick={() => setEditing(!editing)}
          disabled={saving}
        >
          {editing ? "Cancel" : "Edit"}
        </Button>
      </div>

      {editing ? (
        <div style={styles.formStack}>
          <Field label="ESRS standard">
            <Input
              value={editForm.esrsStandard || ""}
              onChange={(event) =>
                setEditForm((current) => ({
                  ...current,
                  esrsStandard: event.target.value,
                }))
              }
            />
          </Field>

          <Field label="Disclosure">
            <Input
              value={editForm.disclosure || ""}
              onChange={(event) =>
                setEditForm((current) => ({
                  ...current,
                  disclosure: event.target.value,
                }))
              }
            />
          </Field>

          <Field label="Section">
            <Input
              value={editForm.section || ""}
              onChange={(event) =>
                setEditForm((current) => ({
                  ...current,
                  section: event.target.value,
                }))
              }
            />
          </Field>

          <Field label="Quote">
            <Textarea
              rows={6}
              value={editForm.quote || ""}
              onChange={(event) =>
                setEditForm((current) => ({
                  ...current,
                  quote: event.target.value,
                }))
              }
            />
          </Field>

          <Field label="Reason">
            <Textarea
              rows={4}
              value={editForm.reason || ""}
              onChange={(event) =>
                setEditForm((current) => ({
                  ...current,
                  reason: event.target.value,
                }))
              }
            />
          </Field>

          <Field label="Confidence">
            <Input
              type="number"
              min="0"
              max="100"
              value={editForm.confidence || 0}
              onChange={(event) =>
                setEditForm((current) => ({
                  ...current,
                  confidence: event.target.value,
                }))
              }
            />
          </Field>

          <Button onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : "Save evidence"}
          </Button>
        </div>
      ) : (
        <>
          <DetailBlock label="Quote" value={`“${evidence.quote || ""}”`} />
          <DetailBlock label="Reason" value={evidence.reason || "Not provided"} />
          <DetailBlock label="Section" value={evidence.section || "Not provided"} />
          <DetailBlock
            label="Document"
            value={evidence.document?.originalName || "Unknown document"}
          />
          <DetailBlock
            label="Report"
            value={
              evidence.report
                ? `${evidence.report.companyName} (${evidence.report.reportingYear})`
                : "Not linked"
            }
          />
          <DetailBlock label="Source" value={evidence.source || "Not provided"} />
          <DetailBlock
            label="Created"
            value={
              evidence.createdAt
                ? new Date(evidence.createdAt).toLocaleString()
                : "Not available"
            }
          />

          <Field label="Review comments">
            <Textarea
              rows={4}
              value={reviewComments}
              onChange={(event) => setReviewComments(event.target.value)}
              placeholder="Add reviewer comments..."
            />
          </Field>

          <div style={styles.buttonRow}>
            <Button onClick={onApprove} disabled={saving}>
              {saving ? "Working..." : "Approve"}
            </Button>
            <Button variant="warning" onClick={onReject} disabled={saving}>
              Reject
            </Button>
            <Button variant="danger" onClick={onDelete} disabled={saving}>
              Delete
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

function DetailBlock({ label, value }) {
  return (
    <div style={styles.detailBlock}>
      <span style={styles.fieldLabel}>{label}</span>
      <p style={styles.detailValue}>{value}</p>
    </div>
  );
}

function Notice({ tone, message }) {
  if (!message) return null;

  const isError = tone === "error";
  return (
    <div
      style={{
        ...styles.notice,
        background: isError ? colors.dangerBg : colors.successBg,
        color: isError ? colors.danger : colors.success,
      }}
    >
      {message}
    </div>
  );
}

function formatStatus(status) {
  return String(status || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusTone(status) {
  if (status === "published" || status === "approved" || status === "attached") {
    return "success";
  }
  if (status === "pending_review") return "warning";
  if (status === "rejected") return "danger";
  return "neutral";
}

const styles = {
  page: {
    minHeight: "100%",
    background: colors.background,
    color: colors.text,
    padding: spacing["2xl"],
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: spacing.lg,
    marginBottom: spacing["2xl"],
  },
  filters: {
    display: "grid",
    gridTemplateColumns: "minmax(240px, 1.5fr) repeat(3, minmax(160px, 1fr)) auto",
    gap: spacing.md,
    alignItems: "center",
  },
  filterActions: {
    display: "flex",
    gap: spacing.sm,
    alignItems: "center",
    flexWrap: "wrap",
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.6fr) minmax(340px, 0.8fr)",
    gap: spacing.xl,
    alignItems: "start",
  },
  notice: {
    borderRadius: 10,
    padding: "12px 16px",
    marginBottom: spacing.lg,
    fontWeight: 700,
  },
  detailHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: spacing.lg,
    alignItems: "flex-start",
    paddingBottom: spacing.lg,
    borderBottom: `1px solid ${colors.border}`,
  },
  detailTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing.sm,
    flexWrap: "wrap",
  },
  detailTitle: {
    margin: 0,
    color: colors.text,
    fontSize: 22,
    lineHeight: 1.3,
  },
  detailMeta: {
    margin: "6px 0 0",
    color: colors.textMuted,
    fontSize: 13,
  },
  formStack: {
    display: "grid",
    gap: spacing.lg,
    marginTop: spacing.lg,
  },
  field: {
    display: "grid",
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  fieldLabel: {
    color: colors.textMuted,
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  detailBlock: {
    paddingTop: spacing.lg,
    marginTop: spacing.lg,
    borderTop: `1px solid ${colors.border}`,
  },
  detailValue: {
    margin: "6px 0 0",
    color: colors.text,
    lineHeight: 1.65,
    wordBreak: "break-word",
  },
  buttonRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: spacing.sm,
    marginTop: spacing.xl,
  },
};
